import { persistentAtom } from '@nanostores/persistent';
import {
  atom,
  computed,
  map,
  type ReadableAtom,
  type WritableAtom,
} from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';
import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import {
  DRAW_TOOLS,
  LIVE_EDGE_SEEK_OFFSET_SECONDS,
  TEXT_SELECT_TOOL,
} from '../constants';
import type { Range, ToolType, WatermarkOptions } from '../types.js';
import { setupGlobalStoreListeners } from './global_store_listeners.js';
import { createCompareStore } from './sub_stores/compare.js';
import { createFullscreenStore } from './sub_stores/fullscreen.js';
import { createNavigationStore } from './sub_stores/navigation.js';
import { createPlayableMediaStore } from './sub_stores/playable_media.js';
import { createPlaylistStore } from './sub_stores/playlist.js';
import { createSegmentStore } from './sub_stores/segment.js';
import { createSettingsStore } from './sub_stores/settings.js';
import { createSizeStore } from './sub_stores/size.js';
import { createSlidesStore } from './sub_stores/slides.js';
import { createSyncStore } from './sub_stores/sync.js';
import { createTimelineStore } from './sub_stores/timeline.js';
import { createTransformationsStore } from './sub_stores/transformations.js';

const logger = DebugLogger.getInstance('unified_player_core');

// Deep transform WritableAtom to ReadableAtom
type TransformWritableToReadable<T> = {
  [P in keyof T]: T[P] extends (...args: any[]) => any
    ? T[P] // Check if property is a function, don't do anything if it is
    : T[P] extends WritableAtom<infer U>
      ? ReadableAtom<U> // Transform WritableAtom to ReadableAtom
      : T[P] extends object
        ? TransformWritableToReadable<T[P]> // Recursively check objects
        : T[P];
};

export type PlayerStore<PlaylistItemMeta = unknown> = ReturnType<
  typeof createPlayerStore<PlaylistItemMeta>
>;

export type ErrorState = {
  state: 'error';
  type:
    | 'drm_output_restricted'
    | 'drm_key_error'
    | 'format_unsupported'
    | 'media_unavailable';
};

export type LoadState = 'loading' | 'loaded' | 'error' | ErrorState | undefined;

export function isErrorLoadState(state: LoadState): boolean {
  return (
    state === 'error' || (typeof state === 'object' && state.state === 'error')
  );
}

export function isMediaUnavailableState(state: LoadState): boolean {
  return (
    typeof state === 'object' &&
    state.state === 'error' &&
    state.type === 'media_unavailable'
  );
}

export function createPlayerStore<PlaylistItemMeta>(playerEvents: EventTarget) {
  logger.debug('Creating Unified Player store');
  const sync = createSyncStore(playerEvents);

  const $activeTool = persistentAtom<ToolType>('UP_activeTool', 'PENCIL');
  const $previousActiveTool = persistentAtom<ToolType>(
    'UP_previousActiveTool',
    'PENCIL',
  );

  const $isDrawingActiveTool = computed($activeTool, (activeTool) =>
    DRAW_TOOLS.some((drawTool) => drawTool === activeTool),
  );

  const $enforceClickToPlay = atom<boolean>(false);
  const setEnforceClickToPlay = (enforce: boolean) => {
    $enforceClickToPlay.set(enforce);
  };

  const playlist = createPlaylistStore<PlaylistItemMeta>(sync.syncClient);
  const playableMedia = createPlayableMediaStore(
    playlist,
    sync.syncClient,
    playerEvents,
  );
  const timeline = createTimelineStore(playlist, playableMedia);
  const slides = createSlidesStore(playlist, playableMedia);
  const compare = createCompareStore<PlaylistItemMeta>(
    sync.syncClient,
    playableMedia.$isLive,
  );
  const fullscreen = createFullscreenStore();
  const size = createSizeStore(playlist);
  const transformations = createTransformationsStore(size, sync.syncClient);
  const settings = createSettingsStore();
  const segment = createSegmentStore(timeline, playableMedia, sync.syncClient);
  const navigation = createNavigationStore();

  const $loadState = atom<LoadState>(undefined);

  function setLoadState(state: LoadState) {
    $loadState.set(state);
  }

  function setDetailedErrorState(type: ErrorState['type']) {
    $loadState.set({ state: 'error', type });
  }

  const $displayedImageUrl = atom<string | undefined>(undefined);

  function setDisplayedImageUrl(url: string | undefined) {
    $displayedImageUrl.set(url);
  }

  function setActiveTool(state: ToolType) {
    const current = $activeTool.get();
    if (current !== 'PLAY' && current !== TEXT_SELECT_TOOL) {
      $previousActiveTool.set(current);
    }
    $activeTool.set(state);
  }

  function getClampingRange(includingSegment: boolean = true) {
    const segmentRange = includingSegment
      ? {
          start: segment.$currentSegment.get()?.range?.start,
          end: segment.$currentSegment.get()?.range?.end,
        }
      : undefined;
    const playableRange = {
      start: playableMedia.$playableRange.get().start,
      end: playableMedia.$lastPlayableFrameTime.get(),
    };

    const firstFrame = segmentRange?.start ?? playableRange.start;
    const lastAvailableFrame = playableMedia.$lastAvailableFrameTime.get();
    const lastFrame = Math.min(
      segmentRange?.end ?? playableRange.end,
      lastAvailableFrame ?? Infinity,
    );
    return { firstFrame, lastFrame };
  }

  function clampPlayableTime(time: number) {
    const { firstFrame, lastFrame } = getClampingRange(false);
    return clamp(time, firstFrame, lastFrame);
  }

  function clampTime(time: number) {
    const { firstFrame, lastFrame } = getClampingRange();
    return clamp(time, firstFrame, lastFrame);
  }

  function handleLooping() {
    const currentTime = playableMedia.$currentTime.get();
    if (currentTime === undefined) {
      return;
    }

    const { start: playableStart } = playableMedia.$playableRange.get();
    const { start: segmentStart, end: segmentEnd } =
      segment.$currentSegment.get().range ?? {};
    const lastPlayableFrameTime = playableMedia.$lastPlayableFrameTime.get();

    const rangeStart = segmentStart ?? playableStart;
    const rangeEnd = segmentEnd ?? lastPlayableFrameTime;
    const targetTime = currentTime <= rangeStart ? rangeEnd : rangeStart;

    setTargetTime(targetTime);
  }
  const $watermarks = atom<string[]>([]);

  const $watermarkOptions = map<WatermarkOptions>({
    position: 'center',
    textOpacity: 0.5,
    shadowOpacity: 0.5,
  });

  function setWatermarks(watermarks: string[]) {
    $watermarks.set(watermarks);
  }

  function setWatermarkOptions(options: WatermarkOptions) {
    $watermarkOptions.set(options);
  }

  function setTargetTime(time: number | undefined) {
    const currentSegmentRange = segment.$currentSegment.get().range;

    const navigatedOutOfCurrentRange =
      currentSegmentRange &&
      !playableMedia.$scrubbing.get() &&
      !playableMedia.$stepping.get() &&
      time !== undefined &&
      (time < currentSegmentRange.start || time > currentSegmentRange.end);

    // Allow stepping out of single frame segments (when for example a comment/marker is selected)
    const navigatedOutOfSingleFrame =
      currentSegmentRange &&
      playableMedia.$stepping.get() &&
      segment.$currentSegmentDuration.get() === 0;

    if (navigatedOutOfCurrentRange || navigatedOutOfSingleFrame) {
      segment.clearCurrentSegment();
    }

    playableMedia.$targetTime.set(time);
    playableMedia.setCurrentTime(time);
  }

  function seekByFrames(frames: number) {
    const currentTime = playableMedia.$currentTime.get();
    if (currentTime === undefined) {
      return;
    }

    const currentItem = playlist.$currentPlaylistItem.get();
    const frameDuration = playlist.$frameDuration.get();
    if (!currentItem) {
      return;
    }
    playableMedia.setStepping(true);
    const time =
      playableMedia.getFrameAlignedTime(currentTime) + frames * frameDuration;
    setTargetTime(time);
  }

  function seekBySeconds(seconds: number) {
    const currentTime = playableMedia.$currentTime.get();
    if (currentTime === undefined) {
      return;
    }

    setTargetTime(currentTime + seconds);
  }

  // Seek to a point on the timeline, [0, 1]
  function seek(progress: number) {
    const targetTime = timeline.progressToTime(progress);
    if (targetTime === undefined) {
      return;
    }
    const frameAlignedTime = playableMedia.getFrameAlignedTime(targetTime);
    setTargetTime(frameAlignedTime);

    // Landing on the live edge stays on live, anything further back holds
    // position for review rather than playing on from behind
    if (playableMedia.$isLive.get()) {
      if (playableMedia.$isAtLiveEdge.get()) {
        playableMedia.setFollowingLive(true);
      } else {
        playableMedia.pause();
      }
    }
  }

  /**
   * Selects a range and optionally puts the playhead on it.
   *
   * The two steps have to happen together and in this order.
   * `setCurrentSegmentRange` clamps to the playable range and frame-aligns, so
   * only it knows where the range ended up, and `setTargetTime` reads a seek
   * outside the current range as leaving the segment and clears it. Seeking to
   * the caller's raw start would therefore sometimes throw the selection away.
   */
  function selectSegmentRange(
    range: Range,
    options: {
      /** Put the range into editing state, as a manual in/out selection is. */
      editing?: boolean;
      /** Move the playhead to the start of the range as it was applied. */
      seek?: boolean;
    } = {},
  ) {
    segment.setCurrentSegmentRange(range);
    if (options.editing) {
      segment.setCurrentSegmentEditing(true);
    }
    if (!options.seek) {
      return;
    }
    const applied = segment.$currentSegment.get().range;
    if (applied) {
      setTargetTime(applied.start);
    }
  }

  /**
   * Moves to the next timeline highlight after the playhead, or the previous
   * one before it, selecting its range. Steps in playback order, not the
   * relevance order the highlights arrived in.
   *
   * Steps from the selected highlight where there is one: until a seek lands
   * the playhead still reads as that highlight's own start or end, so a second
   * press would select it again.
   */
  function selectAdjacentHighlight(direction: 1 | -1) {
    const byTime = [...timeline.$positionedHighlights.get()].sort(
      (one, other) => one.start - other.start,
    );
    if (byTime.length === 0) {
      return;
    }

    const selectedRange = segment.$currentSegment.get().range;
    const from =
      selectedRange && timeline.isHighlightRange(selectedRange)
        ? selectedRange.start
        : playableMedia.$currentTime.get();
    if (from === undefined) {
      return;
    }

    // A frame of slack, so stepping forward off a highlight the playhead is
    // already sitting on does not just re-select it.
    const slack = playlist.$frameDuration.get();
    const next =
      direction === 1
        ? byTime.find(({ range }) => range.start > from + slack)
        : byTime.findLast(({ range }) => range.start < from - slack);
    if (!next) {
      return;
    }
    selectSegmentRange(next.range, { editing: true, seek: true });
  }

  const selectNextHighlight = () => selectAdjacentHighlight(1);
  const selectPreviousHighlight = () => selectAdjacentHighlight(-1);

  function seekToLiveEdge() {
    const liveEdge = playableMedia.$duration.get();
    if (!playableMedia.$isLive.get() || liveEdge === undefined) {
      return;
    }

    setTargetTime(Math.max(0, liveEdge - LIVE_EDGE_SEEK_OFFSET_SECONDS));
    playableMedia.play();
    playableMedia.setFollowingLive(true);
  }

  playableMedia.$shouldBePlaying.listen((shouldBePlaying) => {
    const currentTime = playableMedia.$currentTime.get();
    if (currentTime === undefined) {
      return;
    }

    if (!shouldBePlaying) {
      setTargetTime(currentTime);
    }
  });

  const setCurrentSlide = (slideIndex: number) => {
    setTargetTime(slides.clampSlide(slideIndex) / 1000);
  };

  const previousSlide = () => {
    setCurrentSlide(slides.clampSlide(slides.$currentSlide.get() - 1));
  };

  const nextSlide = () => {
    setCurrentSlide(slides.clampSlide(slides.$currentSlide.get() + 1));
  };

  segment.$currentSegment.listen((newSegment, oldSegment) => {
    const range = newSegment.range;
    if (!range) {
      return;
    }
    const currentTime = playableMedia.$currentTime.get();
    if (currentTime === undefined) {
      setTargetTime(range.start);
      return;
    }
    if (newSegment.id !== oldSegment?.id || currentTime < range.start) {
      setTargetTime(range.start);
    } else if (currentTime > range.end) {
      setTargetTime(range.end);
    }
  });

  const store = {
    playlist,
    playableMedia,
    timeline,
    slides,
    compare,
    fullscreen,
    transformations,
    settings,
    segment,
    navigation,
    size,
    $loadState,
    $displayedImageUrl,
    $activeTool,
    $previousActiveTool,
    $isDrawingActiveTool,
    $watermarks,
    $enforceClickToPlay,
    $watermarkOptions,
    setLoadState,
    setDetailedErrorState,
    setDisplayedImageUrl,
    setActiveTool,
    clampTime,
    clampPlayableTime,
    handleLooping,
    setWatermarks,
    setWatermarkOptions,
    seekByFrames,
    seekBySeconds,
    seek,
    seekToLiveEdge,
    selectSegmentRange,
    selectNextHighlight,
    selectPreviousHighlight,
    setTargetTime,
    setCurrentSlide,
    previousSlide,
    nextSlide,
    sync,
    setEnforceClickToPlay,
  };

  setupGlobalStoreListeners(store);

  // biome-ignore lint/plugin: exposes the store's writable atoms as read-only at the type level; a WritableAtom is a ReadableAtom at runtime, so no runtime narrowing can express this mapped-type transform.
  return store as TransformWritableToReadable<typeof store>;
}
